import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Briefcase,
  ListChecks,
  Clock,
  FileText,
  Wrench,
  Users,
  Building2,
  UserSquare2,
  Settings,
  Palette,
  HelpCircle,
  Inbox,
} from 'lucide-react';
import { cn } from '../../lib/utils';

interface NavItem {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
}

interface NavGroup {
  /** Orients someone who did not build this product. */
  heading: string;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    heading: 'Work',
    items: [
      { label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
      { label: 'Jobs', icon: Briefcase, path: '/jobs' },
      { label: 'Tasks', icon: ListChecks, path: '/tasks' },
      { label: 'Client Requests', icon: Inbox, path: '/client-requests' },
      { label: 'Time Tracking', icon: Clock, path: '/time-tracking' },
      { label: 'Documents', icon: FileText, path: '/documents' },
      { label: 'Services', icon: Wrench, path: '/services' },
    ],
  },
  {
    heading: 'Manage',
    items: [
      { label: 'Users', icon: Users, path: '/users' },
      { label: 'Projects', icon: UserSquare2, path: '/clients' },
      { label: 'Clients', icon: Building2, path: '/organizations' },
    ],
  },
  {
    heading: 'System',
    items: [
      { label: 'Settings', icon: Settings, path: '/settings' },
      { label: 'Design System', icon: Palette, path: '/design-system' },
      { label: 'Help', icon: HelpCircle, path: '/help' },
    ],
  },
];

interface SidebarNavProps {
  mobileOpen?: boolean;
  onMobileOpenChange?: (open: boolean) => void;
}

const NavSection: React.FC<{
  group: NavGroup;
  onNavigate?: () => void;
}> = ({ group, onNavigate }) => (
  <div className="px-3">
    <p className="px-3 pb-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-white/40">
      {group.heading}
    </p>
    <div className="space-y-0.5">
      {group.items.map((item) => (
        <NavLink
          key={item.path}
          to={item.path}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              // No left border: a 4px border shifts the label on activation,
              // so the whole row jitters as you move between pages.
              'group relative flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors duration-150',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-primary',
              isActive
                ? 'bg-white/[0.14] font-semibold text-white'
                : 'font-medium text-white/70 hover:bg-white/[0.07] hover:text-white'
            )
          }
        >
          {({ isActive }) => (
            <>
              <item.icon
                className={cn(
                  'h-5 w-5 shrink-0 transition-colors duration-150',
                  isActive
                    ? 'text-accent'
                    : 'text-white/45 group-hover:text-white/80'
                )}
                aria-hidden="true"
              />
              <span className="truncate">{item.label}</span>
            </>
          )}
        </NavLink>
      ))}
    </div>
  </div>
);

const SidebarContent: React.FC<{ onNavigate?: () => void }> = ({ onNavigate }) => (
  <>
    {/* Logo Section */}
    <div className="flex h-16 shrink-0 items-center justify-center border-b border-white/10 px-6">
      <img
        src="/SentralGroup-Brand-Horizontal.svg"
        alt="Sentral Group"
        className="h-8 w-auto brightness-0 invert"
      />
    </div>

    {/* Navigation Items */}
    <div className="scrollbar-on-dark flex-1 space-y-6 overflow-y-auto py-5">
      {navGroups.map((group) => (
        <NavSection key={group.heading} group={group} onNavigate={onNavigate} />
      ))}
    </div>
  </>
);

export const SidebarNav: React.FC<SidebarNavProps> = ({
  mobileOpen = false,
  onMobileOpenChange,
}) => {
  const closeMobileNav = () => onMobileOpenChange?.(false);

  return (
    <>
      <nav
        className="hidden h-full w-[260px] flex-col border-r border-white/10 bg-primary text-white md:flex"
        aria-label="Main"
      >
        <SidebarContent />
      </nav>

      <div
        className={cn(
          'fixed inset-0 z-40 bg-black/40 transition-opacity md:hidden',
          mobileOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        )}
        onClick={closeMobileNav}
        aria-hidden="true"
      />

      <nav
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-[260px] max-w-[80vw] flex-col border-r border-white/10 bg-primary text-white shadow-xl transition-transform md:hidden',
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        )}
        aria-label="Main"
        aria-hidden={!mobileOpen}
      >
        <SidebarContent onNavigate={closeMobileNav} />
      </nav>
    </>
  );
};
